import { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { isNilOrEmpty, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';
import { useLocalConfirmDialog } from '@gef-ui/material/components/Dialog/ConfirmDialog';

import { ComponentLoader } from 'components';
import fetch from 'features/common/fetch';
import {
	fetchMarketPlaceAgencies,
	startupMarketPlaceAgenciesTab,
} from 'features/views/settings/marketPlaceAgencies/actions';
import { FIELDS, MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME } from 'features/views/settings/marketPlaceAgencies/form';
import {
	getAgencyManagersAndSubordinates,
	getRegions,
	getSegments,
} from 'features/views/settings/marketPlaceAgencies/selectors';

export const Filter = () => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const next = useControlNext(MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME);
	const formIsInitiated = isObject(formData);

	const regions = useSelector(getRegions);
	const segments = useSelector(getSegments);
	const agencyManagersAndSubordinates = useSelector(getAgencyManagersAndSubordinates);

	const [region, setRegion] = useState(null);
	const [segment, setSegment] = useState(null);
	const [touchedRegion, setTouchedRegion] = useState(false);
	const [touchedSegment, setTouchedSegment] = useState(false);

	useEffect(() => {
		if (formIsInitiated) {
			dispatch(startupMarketPlaceAgenciesTab({ setRegion, setSegment }));
		}
	}, [dispatch, formIsInitiated]);

	useEffect(() => {
		if (formIsInitiated && !isNilOrEmpty(formData?.region) && !isNilOrEmpty(formData?.segment)) {
			dispatch(fetchMarketPlaceAgencies({ region: formData?.region, segment: formData?.segment }));
		}
	}, [dispatch, formIsInitiated, formData?.region, formData?.segment]);

	const [confirmDialogRegion, openConfirmChangeOfRegion] = useLocalConfirmDialog({
		backdropClose: false,
		title: getLabelMessage('confirm:doYouWantToLeave'),
		content: getLabelMessage('settings:marketPlaceAgencies.confirmChangeFilterOption'),
		onOk: () => {
			// formField value will be changed therefore new fetch will happen
			next(setValue({ fieldName: FIELDS.region, value: region?.id || null }));
		},
		onCancel: () => {
			if (!isNilOrEmpty(formData?.region)) {
				const previousRegion = regions.find((option) => option?.id === formData?.region);
				!isNilOrEmpty(previousRegion) && setRegion(previousRegion);
			} else {
				setRegion(null);
			}
		},
	});

	const [confirmDialogSegment, openConfirmChangeOfSegment] = useLocalConfirmDialog({
		backdropClose: false,
		title: getLabelMessage('confirm:doYouWantToLeave'),
		content: getLabelMessage('settings:marketPlaceAgencies.confirmChangeFilterOption'),
		onOk: () => {
			// formField value will be changed therefore new fetch will happen
			next(setValue({ fieldName: FIELDS.segment, value: segment?.id || null }));
		},
		onCancel: () => {
			if (!isNilOrEmpty(formData?.segment)) {
				const previousSegment = segments.find((option) => option?.id === formData?.segment);
				!isNilOrEmpty(previousSegment) && setSegment(previousSegment);
			} else {
				setSegment(null);
			}
		},
	});

	const handleChange = useCallback(
		(field) => (e, value) => {
			if (field === FIELDS.region) {
				if (isNilOrEmpty(agencyManagersAndSubordinates)) {
					setRegion(value);
					next(setValue({ fieldName: FIELDS.region, value: value?.id || null }));
				} else {
					setRegion(value);
					openConfirmChangeOfRegion();
				}
			}

			if (field === FIELDS.segment) {
				if (isNilOrEmpty(agencyManagersAndSubordinates)) {
					setSegment(value);
					next(setValue({ fieldName: FIELDS.segment, value: value?.id || null }));
				} else {
					setSegment(value);
					openConfirmChangeOfSegment();
				}
			}
		},
		[next, agencyManagersAndSubordinates, openConfirmChangeOfRegion, openConfirmChangeOfSegment],
	);

	const handleTouchedRegion = useCallback(() => {
		setTouchedRegion(true);
	}, []);

	const handleTouchedSegment = useCallback(() => {
		setTouchedSegment(true);
	}, []);

	const renderInputRegion = useCallback(
		(params) => {
			return (
				<TextField
					{...params}
					label={getLabelMessage('settings:marketPlaceAgencies.region')}
					fullWidth
					required
					color="error"
					slotProps={{
						inputLabel: {
							validation: touchedRegion && isNilOrEmpty(region) ? 'error' : undefined,
						},
						input: {
							...params?.InputProps,
							validation: touchedRegion && isNilOrEmpty(region) ? 'error' : undefined,
						},
					}}
				/>
			);
		},
		[region, touchedRegion],
	);

	const renderInputSegment = useCallback(
		(params) => {
			return (
				<TextField
					{...params}
					label={getLabelMessage('settings:marketPlaceAgencies.segment')}
					fullWidth
					required
					color="error"
					slotProps={{
						inputLabel: {
							validation: touchedSegment && isNilOrEmpty(segment) ? 'error' : undefined,
						},
						input: {
							...params?.InputProps,
							validation: touchedSegment && isNilOrEmpty(segment) ? 'error' : undefined,
						},
					}}
				/>
			);
		},
		[segment, touchedSegment],
	);

	const getOptionLabel = useCallback((option) => option?.shortDescription, []);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.settingsAllowedRegionsAndSegments}>
			{confirmDialogRegion}
			{confirmDialogSegment}
			<Grid container spacing={2}>
				<Grid size={4}>
					<Autocomplete
						size="small"
						value={region}
						label={getLabelMessage('settings:marketPlaceAgencies.region')}
						options={regions}
						renderInput={renderInputRegion}
						onChange={handleChange(FIELDS.region)}
						onBlur={handleTouchedRegion}
						getOptionLabel={getOptionLabel}
						disableClearable
					/>
				</Grid>
				<Grid size={4}>
					<Autocomplete
						size="small"
						value={segment}
						label={getLabelMessage('settings:marketPlaceAgencies.segment')}
						options={segments}
						onChange={handleChange(FIELDS.segment)}
						renderInput={renderInputSegment}
						onBlur={handleTouchedSegment}
						getOptionLabel={getOptionLabel}
						disableClearable
					/>
				</Grid>
			</Grid>
		</ComponentLoader>
	);
};

export default Filter;
